import { useState } from 'react';
import { Icon } from '@hevoio/hevo-ui-toolkit';
import { VPCPeeringConnection, VPCPeeringStatusValue } from '../interface';
import styles from './ConnectivityBanner.module.scss';

function RequestSubmittedBanner() {
  const [showWhy, setShowWhy] = useState(false);

  return (
    <div className={styles.acceptanceBanner} data-id='request-submitted-banner'>
      <Icon name='info' size={1} className={`text-sub-600 ${styles.bannerIcon}`} />
      <div>
        <span>
          Request is under review by our infrastructure team.{' '}
          <button
            type='button'
            className={styles.howToAcceptLink}
            data-id='request-submitted-why-link'
            onClick={() => setShowWhy(value => !value)}
          >
            Why does this happen?
          </button>
        </span>
        {showWhy && (
          <p className={styles.whyExplanation} data-id='request-submitted-why-text'>
            Most VPC peering requests are provisioned automatically within a few minutes. However,
            some requests require manual setup by the Hevo infrastructure team due to network or
            infrastructure requirements. No action is required from your end. The status will
            update automatically once the peering connection is ready.
          </p>
        )}
      </div>
    </div>
  );
}

export interface ConnectivityBannerProps {
  connection: VPCPeeringConnection;
  onHowToAccept: (connection: VPCPeeringConnection) => void;
}

export function ConnectivityBanner({ connection, onHowToAccept }: ConnectivityBannerProps) {
  const status = connection.status?.value;

  if (status === VPCPeeringStatusValue.REQUEST_SUBMITTED) {
    return <RequestSubmittedBanner />;
  }

  // failure_hint is the customer-safe sentence; message is the sanitized
  // backend error and is only worth showing when there is no hint.
  if (status === VPCPeeringStatusValue.FAILED) {
    const reason = connection.failure_hint ?? connection.message;

    if (!reason) {
      return null;
    }

    return (
      <div className={styles.failureBanner} data-id='peering-failure-banner'>
        <Icon name='error-outline' size={1} className={`text-state-error-base ${styles.bannerIcon}`} />
        <span>{reason}</span>
      </div>
    );
  }

  if (status !== VPCPeeringStatusValue.PENDING_ACCEPTANCE) {
    return null;
  }

  return (
    <div className={styles.acceptanceBanner}>
      <Icon name='info' size={1} className={`text-sub-600 ${styles.bannerIcon}`} />
      <span>
        Hevo sent a peering connection request to your AWS account. Accept it in the AWS console to
        finish setup.{' '}
        <button
          type='button'
          className={styles.howToAcceptLink}
          data-id='how-to-accept-link'
          onClick={() => onHowToAccept(connection)}
        >
          How to accept
        </button>
      </span>
    </div>
  );
}
